import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INJECT_MARK = "data-fraseology-injected";

// Upsert a <meta> tag by name OR property.
function upsertMeta({ name, property, content }) {
  if (!content) return;
  const selector = name
    ? `meta[name="${name}"]`
    : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

// Clear any previously-injected head_scripts so edits don't duplicate.
function clearInjectedScripts() {
  document.head
    .querySelectorAll(`[${INJECT_MARK}]`)
    .forEach((n) => n.parentNode && n.parentNode.removeChild(n));
}

// Parse raw HTML and append each element (scripts, noscripts, meta) to document.head.
// Important: <script> elements created via innerHTML do not execute; we must create
// fresh <script> nodes so the browser runs them.
function injectHeadScripts(html) {
  clearInjectedScripts();
  if (!html || !html.trim()) return;
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  for (const node of Array.from(tpl.content.childNodes)) {
    if (node.nodeType !== 1) continue; // element nodes only
    const tag = node.tagName.toLowerCase();
    if (tag === "script") {
      const s = document.createElement("script");
      for (const attr of Array.from(node.attributes)) {
        s.setAttribute(attr.name, attr.value);
      }
      s.text = node.textContent || "";
      s.setAttribute(INJECT_MARK, "");
      document.head.appendChild(s);
    } else {
      const clone = node.cloneNode(true);
      clone.setAttribute && clone.setAttribute(INJECT_MARK, "");
      document.head.appendChild(clone);
    }
  }
}

export function applySiteConfig(cfg) {
  if (!cfg) return;
  if (cfg.site_title) document.title = cfg.site_title;
  upsertMeta({ name: "description", content: cfg.site_description });
  upsertMeta({ property: "og:title", content: cfg.site_title });
  upsertMeta({ property: "og:description", content: cfg.site_description });
  if (cfg.og_image) {
    upsertMeta({ property: "og:image", content: cfg.og_image });
    upsertMeta({ name: "twitter:image", content: cfg.og_image });
  }
  upsertMeta({ name: "twitter:card", content: "summary_large_image" });
  injectHeadScripts(cfg.head_scripts);
}

export default function useSiteConfig() {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site/config`);
        if (!cancelled) applySiteConfig(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[site-config] failed to load; using HTML defaults:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
}
